import React, { useMemo } from 'react';
import { LoadingSpinner } from '../LoadingSpinner';

export enum ButtonVariant {
  primary = 'primary',
  secondary = 'secondary',
  link = 'link',
  linkPrimary = 'linkPrimary',
  danger = 'danger',
  info = 'info',
  filter = 'filter',
}

export interface ButtonProps {
  text?: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  /** Primary button by default */
  variant?: ButtonVariant;
  disabled?: boolean;
  loading?: boolean;
  /** Content to include before button text */
  leading?: React.ReactNode;
  /** Content to include after button text */
  trailing?: React.ReactNode;
  className?: string;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-primary stroke-on-primary px-5 text-on-primary active:bg-primary-600 disabled:bg-gray-200 disabled:stroke-gray-400 disabled:text-gray-400 hover:bg-primary-400',
  secondary:
    'border border-gray-default stroke-gray-default px-5 text-body active:bg-gray-100 hover:bg-gray-50 disabled:border-gray-disabled disabled:text-disabled',
  link: 'px-3 text-body stroke-body disabled:text-gray-400 disabled:stroke-gray-400',
  linkPrimary:
    'stroke-primary-700 px-3 text-primary-700 active:text-primary-900 disabled:stroke-gray-400 disabled:text-gray-400',
  danger:
    'bg-error-400 stroke-on-primary px-5 text-on-primary active:bg-error-600 disabled:bg-gray-200 disabled:stroke-gray-400 disabled:text-gray-400 hover:bg-error-300',
  info: 'bg-blue-600 stroke-white px-5 text-white',
  filter: 'border border-gray-default bg-white stroke-black px-3 text-black hover:bg-gray-default',
} as const;

export function Button({
  text,
  variant = ButtonVariant.primary,
  onClick = () => {},
  disabled = false,
  loading = false,
  leading,
  trailing,
  className = '',
}: ButtonProps) {
  const cursor = useMemo(() => {
    if (loading || disabled) return 'cursor-not-allowed';
    return '';
  }, [loading, disabled]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (loading || disabled) return;

    onClick(event);
  };

  return (
    <button
      disabled={disabled}
      className={`${variantStyles[variant]} ${cursor} ${className} h-10 select-none rounded-2 -outline-offset-1`}
      onClick={handleClick}
    >
      <div className='relative flex items-center justify-center'>
        {loading && (
          <div className='absolute'>
            <LoadingSpinner />
          </div>
        )}
        <div className={`flex items-center justify-center ${loading ? 'invisible' : ''}`}>
          {leading && <div className='pr-2'>{leading}</div>}
          {text && <p className='text-sm'>{text}</p>}
          {trailing && <div className='pl-2'>{trailing}</div>}
        </div>
      </div>
    </button>
  );
}
